import { FEEDBACK_PATH } from '@sh/shared'
import type { Ack } from '@sh/shared'

/** The same eight seconds `request()` waits, because it is the same promise to the same person. */
const TIMEOUT_MS = 8000

/**
 * The one thing this application sends that is not a move in a game.
 *
 * `fetch` rather than the socket, and that is the design rather than a
 * shortcut. `getSocket()` is lazy, and nothing on a page of prose has ever
 * opened a connection - so sending this down a socket would mean standing one
 * up, and a session on the server to hold it, the moment somebody clicks a
 * link in a footer. A POST costs one request and nothing afterwards, and it
 * carries a status code, which is what lets a rate limit say so instead of
 * looking like a dead network.
 *
 * The body is the message and nothing else. Not stripped of a name and a code
 * before sending: never assembled with them in the first place, which is the
 * only version of that promise a reader can check.
 */
export async function sendFeedback(message: string): Promise<Ack<null>> {
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS)
  try {
    const response = await fetch(FEEDBACK_PATH, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // Nothing this browser happens to be carrying rides along on a message
      // that is supposed to arrive attached to nobody.
      credentials: 'omit',
      body: JSON.stringify({ message }),
      signal: abort.signal,
    })
    if (response.ok) return { ok: true, data: null }
    if (response.status === 429) {
      return { ok: false, error: 'that was a lot at once - give it a minute and try again' }
    }
    // Everything else, including the 404 a deployment with no ntfy answers
    // with. The distinction is the operator's to care about, not the player's,
    // and the useful half of the sentence is the second one.
    return { ok: false, error: 'that did not get through - your words are still here' }
  } catch {
    return { ok: false, error: 'that did not get through - your words are still here' }
  } finally {
    clearTimeout(timer)
  }
}
